import { Controller } from "@hotwired/stimulus"
import { onHeaderSignup } from "../lib/header_signup"
import { fitHeightBelowHeader } from "../lib/fit_viewport"
import { formatLength } from "../lib/preferences"

// Time demo: a fake crew clock-in on the phone that appears on a fake manager view.
// Nothing is saved; the visitor's email is asked for only after the payoff.
const SITE_RADIUS_M = 150
const GPS_DISTANCE_M = 12
const MOBILE_QUERY = '(max-width: 767px)'
const MIN_FIT_SCALE = 0.5
const SWITCH_TO_MANAGER_DELAY_MS = 1400

export default class extends Controller {
  static targets = [
    "grid", "toggleBtn", "title", "lead", "step", "ask",
    "clockinScreen", "doneScreen", "nameInput", "role", "clockHm", "clockSecs",
    "clockInBtn", "gpsDistance", "doneWho", "doneStart",
    "boundaryLabel", "youDot", "empty", "youRow", "youName", "youRole", "youTime"
  ]
  static values = { signedIn: Boolean, dashboardUrl: String }

  connect() {
    this.clockedIn = false
    this.tick()
    this.clockTimer = setInterval(() => this.tick(), 1000)
    this.renderUnits()
    this.fitToViewport()
    this.onResize = () => this.fitToViewport()
    window.addEventListener('resize', this.onResize)

    this.onUnitsChanged = () => this.renderUnits()
    window.addEventListener('ge:units-changed', this.onUnitsChanged)
    window.addEventListener('storage', this.onUnitsChanged)

    // The header's "Sign up free" button opens this modal while the Time tab is showing
    this.stopHeaderSignup = onHeaderSignup(this.element, () => this.openSignup())
  }

  disconnect() {
    clearInterval(this.clockTimer)
    clearTimeout(this.switchTimer)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('ge:units-changed', this.onUnitsChanged)
    window.removeEventListener('storage', this.onUnitsChanged)
    this.stopHeaderSignup()
  }

  // On desktop the whole demo has to be visible without scrolling: size the panel to what is
  // left of the window under the header, then shrink the grid until it fits inside it.
  fitToViewport() {
    this.gridTarget.style.zoom = ''
    if (window.matchMedia(MOBILE_QUERY).matches) return

    const available = fitHeightBelowHeader(this.element, '--td-height')
    if (available === null) return

    const natural = this.gridTarget.offsetHeight
    if (natural > available) this.gridTarget.style.zoom = Math.max(MIN_FIT_SCALE, available / natural).toFixed(3)
  }

  nameChanged() {
    const hasName = this.nameInputTarget.value.trim().length > 0
    this.clockInBtnTarget.disabled = !hasName
    this.setStep('selfie', hasName)
  }

  pickRole(event) {
    this.roleTargets.forEach(chip => chip.classList.toggle('td__role--active', chip === event.currentTarget))
  }

  clockIn() {
    if (this.clockedIn) return
    this.clockedIn = true

    const name = this.nameInputTarget.value.trim() || 'Sam Carter'
    const role = this.activeRole()
    const time = this.formatTime(new Date())

    // Phone
    this.doneWhoTarget.textContent = `${name} · ${role}`
    this.doneStartTarget.textContent = `Started ${time}`
    this.clockinScreenTarget.classList.add('d-none')
    this.doneScreenTarget.classList.remove('d-none')

    // Manager view: textContent only, the name is whatever the visitor typed
    this.youNameTarget.textContent = name
    this.youRoleTarget.textContent = role
    this.youTimeTarget.textContent = `${time} · `
    this.youRowTarget.classList.remove('d-none')
    this.youDotTarget.classList.remove('d-none')
    this.emptyTarget.classList.add('d-none')

    // Left column
    this.setStep('clock-in', true)
    this.setStep('watch', true)
    this.titleTarget.textContent = "That's what your crew would see."
    this.leadTarget.textContent = 'Your manager sees the name, selfie, time and GPS the second it happens. No timesheets, no chasing.'
    this.askTarget.classList.remove('d-none')
    this.fitToViewport()

    if (window.matchMedia(MOBILE_QUERY).matches) {
      this.switchTimer = setTimeout(() => this.setView('manager'), SWITCH_TO_MANAGER_DELAY_MS)
    }
  }

  showView(event) {
    this.setView(event.currentTarget.dataset.view)
  }

  openSignup() {
    // A signed-in visitor already has an account: send them to their own tracker
    if (this.signedInValue) {
      window.location.href = this.dashboardUrlValue
      return
    }

    const modal = this.element.querySelector('.signup-modal')
    this.application.getControllerForElementAndIdentifier(modal, 'signup-modal').open()
  }

  setView(view) {
    this.gridTarget.dataset.view = view
    this.toggleBtnTargets.forEach(btn => btn.classList.toggle('td__toggle-btn--active', btn.dataset.view === view))
  }

  setStep(key, done) {
    const step = this.stepTargets.find(el => el.dataset.step === key)
    step.classList.toggle('td__step--done', done)
    step.querySelector('.td__step-mark').textContent = done ? '✓' : this.stepTargets.indexOf(step) + 1
  }

  activeRole() {
    return this.roleTargets.find(chip => chip.classList.contains('td__role--active')).textContent.trim()
  }

  tick() {
    const now = new Date()
    this.clockHmTarget.textContent = this.formatTime(now)
    this.clockSecsTarget.textContent = `:${String(now.getSeconds()).padStart(2, '0')}`
  }

  formatTime(date) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }

  // Boundary label and GPS distance follow the header's m | ft choice
  renderUnits() {
    this.boundaryLabelTarget.textContent = `Site boundary · ${formatLength(String(SITE_RADIUS_M))}`
    this.gpsDistanceTarget.textContent = formatLength(String(GPS_DISTANCE_M))
  }
}
